import React, { forwardRef, useContext, useState } from 'react';

import cx from 'classnames';
import { uniq } from 'ramda';

import FocalImage from 'components/Layouts/Form/FormBuilder/FocalImage';
import useMobileNav from 'components/Layouts/Form/FormBuilder/MobileNav/functions';
import Item from 'components/Layouts/Form/FormBuilder/MobileNav/Item';
import flightDefault from 'components/Pages/Admin/GuestPortal/Landing/flights_default.jpg';
import { IWay } from 'components/Pages/Admin/shared/ItinerariesItemLanding/Builder/types';
import Button from 'components/shared/Button';
import CloseBurger from 'components/shared/Icon/icons/CloseBurger';
import basePreview from 'components/shared/images/authbg.jpg';
import { LANDING_PAGES_CONTAINER_ID } from 'components/shared/LandingPage/components/Grid';
import { getClassNameByLandingPageType } from 'components/shared/LandingPage/utils';
import { ITINERARIES_ITEM_TYPES, LANDING_PAGE_TYPES } from 'constants/index';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useScrollOffset } from 'hooks/useScrollOffset';
import { useTitleChangeModal } from 'hooks/useTitleChangeModal';
import i18n from 'i18n';
import { getAssetSrc, getAssetSrcFromId } from 'utils/asset';
import { getDateFormat } from 'utils/date';
import { BOATS_THEME_COVER_LEFT_ID, LANDING_THEMES } from 'utils/itineraries/functions';
import {
  formatDatesRangeWithoutParseISO,
  formatDaysRange,
  formatTimeRangeAdapter,
  getTimeRangeAdapter,
} from 'utils/itineraryDetails';
import { SCHEDULE_TYPES } from 'utils/itineraryDetails/constants';
import { EProposalItems, IPage } from 'utils/itineraryDetails/types';

import LandingPageContext from '../contexts/LandingPageContext';
import LandingPageTypeContext from '../contexts/LandingPageTypeContext';
import StartArrow from '../RightSide/StartArrow';
import CoverHeadline from './Headline';
import s from './index.module.css';
import Location from './Location';
import placeholderSrc from './placeholder.svg';
import Subheadline from './Subheadline';
import TravelDates from './TravelDates';
import { ILeftSideProps, INavigationProps } from './types';

const LeftSide = forwardRef<HTMLDivElement, ILeftSideProps>(({ blockId, index = 0 }, ref) => {
  const {
    page,
    landingData,
    landingPageType,
    readOnly,
    onReplaceCover,
    isItinerariesItem,
    onChangeLeftSideText,
    itinerariesItemType,
    isPortal,
    landingTheme,
    screens,
    isInsidePageBuilder = false,
    virtualized,
  } = useContext(LandingPageContext);
  const {
    leftSubheadline,
    getLeftSubheadline,
    getLeftHeadline,
    headlineHint,
    leftClassName,
    showTravelDates,
    notEditLeftSide = false,
    editHeadline = false,
    subheadlineEditable = false,
  } = useContext(LandingPageTypeContext);
  const showNavigationModal = useTitleChangeModal();

  const {
    headline = '',
    subheadline = '',
    primaryImageId,
    location,
    primaryImageCoords,
    travelDates,
    primaryImageVersion400,
    primaryImageVersion800,
    primaryImageVersion1200,
    primaryImageVersion1600,
    primaryImageVersion2400,
  } = landingData;
  const isTimeRange =
    isItinerariesItem &&
    itinerariesItemType === ITINERARIES_ITEM_TYPES.ITINERARY &&
    landingPageType &&
    [LANDING_PAGE_TYPES.PROPERTY, LANDING_PAGE_TYPES.AREA, LANDING_PAGE_TYPES.ACTIVITY].includes(landingPageType);
  const isRelocationsLanding = 'role' in landingData && landingData?.role === 'relocations';
  const previewImage = isRelocationsLanding ? flightDefault : basePreview;
  const isBoatsThemeCoverPage =
    landingPageType === LANDING_PAGE_TYPES.COVER_PAGE && landingTheme === LANDING_THEMES.boats;
  const isGuestPortal = page?.type === EProposalItems.GuestPortal;
  const isCoverPage = landingPageType === LANDING_PAGE_TYPES.COVER_PAGE;

  const getHeadlineValue = () => {
    // @ts-ignore
    if (isItinerariesItem) return headline || getLeftHeadline({ page });
    // @ts-ignore
    return page?.name || getLeftHeadline({ page }) || page?.title;
  };
  const getSubheadlineValue = () => {
    if (isTimeRange && 'scheduleType' in landingData) {
      return formatTimeRangeAdapter(
        landingData?.scheduleType,
        getTimeRangeAdapter(landingData?.scheduleType, landingData as any) as any,
        {
          includeOnlyDifferentYears: true,
          translate: true,
          translationFormat: '%B %-d',
          // @ts-ignore
          translationDateFormat: getDateFormat(page?.settings?.displayDatesDdMmYyyy),
        },
      );
    }
    if (isItinerariesItem && landingPageType === LANDING_PAGE_TYPES.COVER_PAGE) {
      return getLeftSubheadline({ page });
    }
    return subheadline || leftSubheadline || getLeftSubheadline({ page });
  };
  const getTravelDatesValue = () => {
    if (isItinerariesItem && landingPageType === LANDING_PAGE_TYPES.COVER_PAGE && 'travelDates' in page) {
      return page?.travelDates;
    }
    return travelDates;
  };
  const getLocationValue = () => {
    if (isItinerariesItem && landingPageType === LANDING_PAGE_TYPES.SAFARI_MAP) {
      const mapLocations =
        'way' in page
          ? page?.way?.map((item: IWay) => item?.countryName).filter((item?: string) => item !== undefined)
          : [];
      return uniq(mapLocations).join(' & ');
    }
    return location;
  };
  const headlineValue = getHeadlineValue();
  const subheadlineValue = getSubheadlineValue();
  const travelDatesValue = getTravelDatesValue();
  const locationValue = getLocationValue();
  const blockName =
    landingPageType === LANDING_PAGE_TYPES.COVER_PAGE && landingTheme === LANDING_THEMES.boats
      ? BOATS_THEME_COVER_LEFT_ID
      : '';

  const pictureSources = [
    {
      media: '(max-width: 768px)',
      srcSet: getAssetSrc(primaryImageVersion400, primaryImageVersion800, primaryImageId),
    },
    {
      media: '(max-width: 1500px)',
      srcSet: getAssetSrc(primaryImageVersion800, primaryImageVersion1600, primaryImageId),
    },
    {
      media: '(min-width: 1501px)',
      srcSet: getAssetSrc(primaryImageVersion1200, primaryImageVersion2400, primaryImageId),
    },
  ];

  const handleHeaderClick = () => {
    if (isGuestPortal) return;
    if (!isCoverPage) return;

    showNavigationModal(readOnly);
  };

  return (
    <div
      id={blockId}
      data-index={index}
      className={cx(
        s.left,
        leftClassName,
        isPortal && s.isPortal,
        'custom-left',
        getClassNameByLandingPageType(landingPageType, 'left'),
      )}
      ref={ref}
      // @ts-ignore
      name={blockName}
      style={{
        ...(virtualized?.rendered && { position: 'sticky' }),
        ...(virtualized && !virtualized?.rendered && { position: 'relative' }),
      }}
    >
      <div className={`${s.content} custom-left-content`}>
        {!primaryImageId && !isRelocationsLanding && (
          <img src={placeholderSrc} alt="Placeholder" className={s.placeholder} />
        )}
        {/*@ts-ignore*/}
        <FocalImage
          className={`${s.img} custom-left-img`}
          point={primaryImageCoords}
          src={primaryImageId ? getAssetSrcFromId(primaryImageId) : previewImage}
          noLazyLoading
          // defaultSrc={previewUrl}
          pictureSources={pictureSources}
        />
        {!readOnly && (
          <Button
            tabIndex={-1}
            children={primaryImageId || isRelocationsLanding ? 'Replace Photo' : 'Choose Photo'}
            className={cx(s.button, { [s.canBeHidden]: Boolean(primaryImageId) }, 'custom-left-button')}
            // @ts-ignore
            size="medium"
            onClick={onReplaceCover}
            type="default"
          />
        )}

        <Subheadline
          value={subheadlineValue}
          readOnly={readOnly}
          canEdit={
            (subheadlineEditable && isInsidePageBuilder) || (isItinerariesItem && !notEditLeftSide && !isTimeRange)
          }
          onEdit={onChangeLeftSideText}
          onClick={handleHeaderClick}
        />

        {(!readOnly || headlineValue) && (
          <CoverHeadline
            title={headlineValue as string}
            readOnly={readOnly}
            headlineHint={headlineHint}
            canEdit={editHeadline || (isItinerariesItem && !notEditLeftSide)}
            onEdit={onChangeLeftSideText}
            landingTheme={landingTheme ? landingTheme : ''}
          />
        )}

        {showTravelDates && (
          <TravelDates
            value={travelDatesValue}
            readOnly={readOnly}
            canEdit={isItinerariesItem && !notEditLeftSide}
            onEdit={onChangeLeftSideText}
            onClick={handleHeaderClick}
          />
        )}

        {locationValue && (
          <div className={cx(s.locationWrapper, 'custom-left-location-wrapper')}>
            <Location value={locationValue} onEdit={onChangeLeftSideText} readOnly={readOnly} />
          </div>
        )}

        {isBoatsThemeCoverPage && screens && <StartArrow to={screens[1]} containerId={LANDING_PAGES_CONTAINER_ID} />}
      </div>
    </div>
  );
});

export default LeftSide;

export const Navigation = ({ links, isOpen, setIsOpen, classname }: INavigationProps) => {
  const { onActive } = useMobileNav();
  const { isTabletOrMobile } = useMediaBreakpoints();
  const { isItinerariesItem, isPortal, hasHeader, isPortalItinerary, landingTheme, page } =
    useContext(LandingPageContext);
  const [activeIndex, setActiveIndex] = useState('');

  const isIphone = 'iPhone'.match(navigator.platform);

  const { baseOffset, clickOffset } = useScrollOffset({ landingTheme, isPortal, isItinerariesItem, hasHeader });

  const filteredPage =
    'landing' in page ? page.landing.pages.filter((el: IPage) => !el.empty).filter((el: IPage) => !el.hidden) : [];

  const getDates = (value: string, index: number) => {
    const foundPage = filteredPage.find((el: any, i: number) => {
      return el.headline === value && i === index;
    });

    if (foundPage) {
      return foundPage.scheduleType === SCHEDULE_TYPES.DAYS
        ? formatDaysRange({ start: foundPage.daysRange?.start, end: foundPage.daysRange?.end })
        : formatDatesRangeWithoutParseISO(foundPage.datesRange, {
            // @ts-ignore
            translationDateFormat: getDateFormat(page?.settings?.displayDatesDdMmYyyy),
            // @ts-ignore
            translate: page?.settings?.language,
          });
    }
  };

  return (
    <div className={cx(s.container, isOpen && s.isVisible)}>
      <div className={s.navigationContainer}>
        <div className={cx(s.navigation, classname)}>
          {isTabletOrMobile && (
            <div className={s.closeBurgerIcon} onClick={() => setIsOpen(false)}>
              <CloseBurger />
            </div>
          )}
          <div data-block className={cx(s.navigationHeader, 'custom-landing-page-burger-header')}>
            {i18n.t('proposal_content.table_of_contents')}
          </div>
          <div className={s.hideNavigation}></div>
          <div
            className={cx(
              s.leftSideNavigation,
              isIphone && s.iPhoneLeftSideNavigation,
              'custom-landing-page-burger-nav',
            )}
            id="mobileNav"
          >
            {links.map((link: string, i: number) => {
              return (
                <Item
                  key={i}
                  index={i}
                  link={link}
                  baseOffset={baseOffset}
                  clickOffset={clickOffset}
                  onActive={onActive}
                  isPortalItinerary={isPortalItinerary}
                  leftSideNavigation
                  page={page}
                  getDates={getDates}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  setIsOpen={setIsOpen}
                />
              );
            })}
          </div>
        </div>
        <div className={s.navigationRightSide}></div>
      </div>
    </div>
  );
};
